// ERP-Steuerung: Auftragsbestand, Nachkalkulation, Kapazitaet, Liquiditaet
// (ERP/CRM-Plan Phase 5 — docs/erp-crm-integration-plan.md, Abschnitt 8).
//
// PUR. Kein Supabase, kein DOM, kein Date.now(): jede Funktion bekommt ihre
// Zeilen und ihren Stichtag als Argument. Das Laden (RPCs, Zusammenfuehrung
// mit Kundenstamm und Freelancern) steckt in erpSteeringService.ts — dieselbe
// Trennung wie crmMapData.ts/useCrmMap.ts und der Grund, warum dieses Modul
// ohne jsdom testbar ist (Standing Decision „duenne Container, Logik in Hooks
// + pure Funktionen").
//
// BETRAEGE SIND CENT. Ganzzahlig, nie Euro-Fliesskomma. Die einzige Stelle,
// an der hier ein Bruch entsteht, sind Prozentsaetze und die Reichweite in
// Monaten — beide sind Anzeigewerte und werden als solche zurueckgegeben.
//
// Die vier Auswertungen bauen aufeinander NICHT auf; sie teilen sich nur die
// Datumshelfer am Kopf der Datei.

import { IntervalConfig, IntervalType } from '../types';
import { PlzMapping, RegionLevel, UNBEKANNT_KEY, regionKeyForZip } from './crmMapData';

// ═══════════════════════════════════════════════════════════════════════════
// Gemeinsame Helfer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mittlere Monatslaenge in Tagen. Wortgleich mit `phaseInspectionCount` in
 * quoteLogic.ts — beide rechnen Begehungen aus Tagesspannen, und zwei
 * verschiedene Monatslaengen im selben Haus waeren eine Fehlerquelle, die
 * niemand findet.
 */
export const TAGE_PRO_MONAT = 30.44;

/**
 * Arbeitstage je Monat fuer den Intervalltyp `daily`. quoteLogic zaehlt
 * „taeglich" ueber echte Arbeitstage (`workingDays`); hier gibt es keine
 * konkrete Spanne, sondern nur einen Monat, deshalb der Mittelwert
 * 5/7 × 30.44 ≈ 21,7. Bewusst kein Feiertagskalender: das Kapazitaetsbild
 * entscheidet ueber eine Ampelfarbe, nicht ueber eine Rechnung.
 */
export const ARBEITSTAGE_PRO_MONAT = 21.74;

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** ISO-Tagesanteil eines beliebigen Zeitstempels; leer => undefined. */
const tag = (iso?: string | null): string | undefined => {
    const s = String(iso ?? '').trim();
    return s ? s.slice(0, 10) : undefined;
};

/**
 * 'YYYY-MM-DD' als UTC-Millisekunden. UTC und nicht lokal, weil sonst in
 * Zeitzonen oestlich von Greenwich der 1. eines Monats auf den letzten des
 * Vormonats rutscht — derselbe Fehler, der Wochenraster reihenweise um eins
 * verschiebt.
 */
const msVonTag = (iso: string): number | null => {
    const s = tag(iso);
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const ms = Date.parse(`${s}T00:00:00Z`);
    return Number.isNaN(ms) ? null : ms;
};

const TAG_MS = 86_400_000;

const tagVonMs = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const plusTage = (iso: string, tage: number): string => {
    const ms = msVonTag(iso);
    return ms === null ? iso : tagVonMs(ms + tage * TAG_MS);
};

/** Montag der Woche, in der `iso` liegt (ISO-8601: Woche beginnt Montag). */
const montagDerWoche = (iso: string): string => {
    const ms = msVonTag(iso);
    if (ms === null) return iso;
    const wochentag = new Date(ms).getUTCDay(); // 0 = Sonntag
    const versatz = wochentag === 0 ? 6 : wochentag - 1;
    return tagVonMs(ms - versatz * TAG_MS);
};

/** 'YYYY-MM' eines Tages. */
const monatVonTag = (iso: string): string => iso.slice(0, 7);

/** Erster Tag des Monats 'YYYY-MM'. */
const monatsStart = (monat: string): string => `${monat}-01`;

/** Letzter Tag des Monats 'YYYY-MM'. */
const monatsEnde = (monat: string): string => {
    const jahr = Number(monat.slice(0, 4));
    const m = Number(monat.slice(5, 7));
    // Tag 0 des Folgemonats = letzter Tag dieses Monats.
    return new Date(Date.UTC(jahr, m, 0)).toISOString().slice(0, 10);
};

const monatPlus = (monat: string, n: number): string => {
    const jahr = Number(monat.slice(0, 4));
    const m = Number(monat.slice(5, 7));
    const d = new Date(Date.UTC(jahr, m - 1 + n, 1));
    return d.toISOString().slice(0, 7);
};

/** Anzahl Tage im Monat 'YYYY-MM'. */
const tageImMonat = (monat: string): number => Number(monatsEnde(monat).slice(8, 10));

/**
 * Ueberlappung zweier inklusiver Tagesspannen in TAGEN. 0 ohne Ueberschneidung.
 * `undefined` an einer Grenze heisst „offen".
 */
const ueberlappungTage = (
    aVon: string | undefined,
    aBis: string | undefined,
    bVon: string,
    bBis: string,
): number => {
    const von = aVon && aVon > bVon ? aVon : bVon;
    const bis = aBis && aBis < bBis ? aBis : bBis;
    const vonMs = msVonTag(von);
    const bisMs = msVonTag(bis);
    if (vonMs === null || bisMs === null || bisMs < vonMs) return 0;
    return Math.round((bisMs - vonMs) / TAG_MS) + 1;
};

/** Die spaetere von zwei (moeglicherweise offenen) Untergrenzen. */
const spaeterer = (a?: string, b?: string): string | undefined =>
    a && b ? (a > b ? a : b) : a || b;

/** Die fruehere von zwei (moeglicherweise offenen) Obergrenzen. */
const frueherer = (a?: string, b?: string): string | undefined =>
    a && b ? (a < b ? a : b) : a || b;

const prozent = (teil: number, ganzes: number): number | null =>
    ganzes > 0 ? (teil / ganzes) * 100 : null;

// ═══════════════════════════════════════════════════════════════════════════
// 1) Auftragsbestand
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Zeile aus der RPC `erp_auftragsbestand` (bereits camelCase gemappt). */
export interface AuftragsbestandRow {
    /** null = Sammelzeile: Belege ohne projektscharfe Zuordnung. */
    projectId: string | null;
    projektNummer?: string;
    projektName?: string;
    projektStatus?: string;
    kundeId?: string;
    kundeName?: string;
    auftragswertCents: number;
    /** Gesetzt = der Auftragswert wurde bewusst fixiert (auch bei 0 EUR). */
    auftragswertFixiertAm?: string;
    fakturiertCents: number;
    /** Fakturiert in den letzten 12 Monaten — Grundlage der Reichweite. */
    fakturiert12mCents: number;
    offenCents: number;
    rechnungen: number;
    letzteRechnung?: string;
}

export interface AuftragsbestandZeile extends AuftragsbestandRow {
    projectId: string;
    /** Ohne fixierten Auftragswert ist „offen" keine Aussage, sondern Rauschen. */
    hatAuftragswert: boolean;
    /** Fakturiert/Auftragswert in Prozent; null ohne Auftragswert. */
    fortschrittProzent: number | null;
    /** Mehr fakturiert als beauftragt — Hinweis auf fehlenden Nachtrag. */
    ueberfakturiert: boolean;
}

export interface Auftragsbestand {
    zeilen: AuftragsbestandZeile[];
    auftragswertSummeCents: number;
    fakturiertSummeCents: number;
    /** Summe NUR ueber Projekte mit fixiertem Auftragswert. */
    offenSummeCents: number;
    /** Ø Monatsfakturierung der letzten 12 Monate. */
    monatsdurchschnittCents: number;
    /** Offener Bestand / Ø-Monatsfakturierung. null ohne Durchschnitt. */
    reichweiteMonate: number | null;
    /** Projekte mit Rechnungen, aber ohne fixierten Auftragswert. */
    ohneAuftragswert: number;
    /** Nicht projektscharfe Sammelrechnungen (Ehrlichkeitsposten). */
    sammelrechnungenCents: number;
    sammelrechnungen: number;
}

const MONATE_JAHR = 12;

/**
 * Auftragsbestand aus den RPC-Zeilen.
 *
 * Drei Festlegungen, die man kennen muss:
 *
 * 1. `offenSummeCents` summiert NUR Projekte mit fixiertem Auftragswert. Ein
 *    Projekt ohne Auftragswert liefert `offen = 0 − fakturiert`, also einen
 *    grossen negativen Betrag; mitgezaehlt fraesse es den Bestand auf und die
 *    Zahl waere unbrauchbar, ohne dass es jemandem auffiele. Die Luecke
 *    verschwindet nicht, sie wird als `ohneAuftragswert` ausgewiesen.
 *
 * 2. Die Ø-Monatsfakturierung zaehlt ALLE Zeilen mit, auch die
 *    Sammelrechnungen: sie misst den Durchsatz des Bueros, und der Durchsatz
 *    ist unabhaengig davon, ob ein Beleg projektscharf zugeordnet werden kann.
 *
 * 3. Die Sammelzeile (projectId null) steht nicht in `zeilen` — sie ist kein
 *    Projekt. Ihr Betrag steht in `sammelrechnungenCents`, damit die Ansicht
 *    ihn zeigen kann, statt dass die Summen unerklaerlich auseinanderlaufen.
 */
export const berechneAuftragsbestand = (
    rows: AuftragsbestandRow[] | null | undefined,
): Auftragsbestand => {
    const zeilen: AuftragsbestandZeile[] = [];
    let auftragswertSummeCents = 0;
    let fakturiertSummeCents = 0;
    let offenSummeCents = 0;
    let fakturiert12mSumme = 0;
    let ohneAuftragswert = 0;
    let sammelrechnungenCents = 0;
    let sammelrechnungen = 0;

    for (const row of rows || []) {
        const fakturiert = zahl(row?.fakturiertCents);
        fakturiert12mSumme += zahl(row?.fakturiert12mCents);

        if (!row?.projectId) {
            sammelrechnungenCents += fakturiert;
            sammelrechnungen += zahl(row?.rechnungen);
            fakturiertSummeCents += fakturiert;
            continue;
        }

        const auftragswert = zahl(row.auftragswertCents);
        const hatAuftragswert = !!row.auftragswertFixiertAm;

        fakturiertSummeCents += fakturiert;
        if (hatAuftragswert) {
            auftragswertSummeCents += auftragswert;
            offenSummeCents += auftragswert - fakturiert;
        } else {
            ohneAuftragswert += 1;
        }

        zeilen.push({
            ...row,
            projectId: row.projectId,
            auftragswertCents: auftragswert,
            fakturiertCents: fakturiert,
            fakturiert12mCents: zahl(row.fakturiert12mCents),
            offenCents: auftragswert - fakturiert,
            rechnungen: zahl(row.rechnungen),
            hatAuftragswert,
            fortschrittProzent: hatAuftragswert ? prozent(fakturiert, auftragswert) : null,
            ueberfakturiert: hatAuftragswert && fakturiert > auftragswert,
        });
    }

    // Groesster offener Rest zuerst; Projekte ohne Auftragswert ans Ende, sie
    // tragen keine belastbare Zahl und sollen das Ranking nicht anfuehren.
    zeilen.sort((a, b) => {
        if (a.hatAuftragswert !== b.hatAuftragswert) return a.hatAuftragswert ? -1 : 1;
        if (b.offenCents !== a.offenCents) return b.offenCents - a.offenCents;
        return (a.projektNummer || '').localeCompare(b.projektNummer || '');
    });

    const monatsdurchschnittCents = Math.round(fakturiert12mSumme / MONATE_JAHR);

    return {
        zeilen,
        auftragswertSummeCents,
        fakturiertSummeCents,
        offenSummeCents,
        monatsdurchschnittCents,
        // Ein negativer oder leerer Bestand hat keine Reichweite — null statt
        // einer Zahl, die niemand deuten kann.
        reichweiteMonate:
            monatsdurchschnittCents > 0 && offenSummeCents > 0
                ? offenSummeCents / monatsdurchschnittCents
                : null,
        ohneAuftragswert,
        sammelrechnungenCents,
        sammelrechnungen,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 2) Nachkalkulation
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Zeile aus der RPC `erp_nachkalkulation` (bereits camelCase gemappt). */
export interface NachkalkulationRow {
    projectId: string;
    projektNummer?: string;
    projektName?: string;
    kundeId?: string;
    kundeName?: string;
    erloeseCents: number;
    freelancerKostenCents: number;
    begehungen: number;
    /** Begehungen, fuer deren Durchfuehrenden kein Satz gepflegt ist. */
    begehungenOhneSatz: number;
    eigenleistungCents: number;
    deckungsbeitragCents: number;
}

/** Das, was der Kundenstamm zur Gruppierung beitraegt. Mehr braucht es nicht. */
export interface NachkalkulationKunde {
    id: string;
    name?: string;
    branche?: string;
    zip?: string;
}

export interface NachkalkulationZeile extends NachkalkulationRow {
    /** DB/Erloese in Prozent; null ohne Erloes im Zeitraum. */
    margeProzent: number | null;
    branche?: string;
    regionKey?: string;
}

export interface NachkalkulationGruppe {
    key: string;
    label: string;
    projekte: number;
    begehungen: number;
    erloeseCents: number;
    freelancerKostenCents: number;
    eigenleistungCents: number;
    deckungsbeitragCents: number;
    margeProzent: number | null;
}

export interface Nachkalkulation {
    projekte: NachkalkulationZeile[];
    jeKunde: NachkalkulationGruppe[];
    jeBranche: NachkalkulationGruppe[];
    jeRegion: NachkalkulationGruppe[];
    gesamt: NachkalkulationGruppe;
    /** Summe ueber alle Projekte — macht die Luecke in effort_rates sichtbar. */
    begehungenOhneSatz: number;
}

export interface NachkalkulationOptionen {
    /** PLZ→Kreis/Bundesland. Fehlt es, landet alles in „unbekannt". */
    plzMapping?: PlzMapping | null;
    /** Default 'bundesland' — Kreise sind fuer eine DB-Tabelle zu fein. */
    regionLevel?: RegionLevel;
    /** Anzeigename ohne gepflegte Branche. */
    ohneBrancheLabel?: string;
    /** Anzeigename ohne zuordenbare Region. */
    ohneRegionLabel?: string;
}

const leereGruppe = (key: string, label: string): NachkalkulationGruppe => ({
    key,
    label,
    projekte: 0,
    begehungen: 0,
    erloeseCents: 0,
    freelancerKostenCents: 0,
    eigenleistungCents: 0,
    deckungsbeitragCents: 0,
    margeProzent: null,
});

const addiere = (g: NachkalkulationGruppe, z: NachkalkulationZeile): void => {
    g.projekte += 1;
    g.begehungen += z.begehungen;
    g.erloeseCents += z.erloeseCents;
    g.freelancerKostenCents += z.freelancerKostenCents;
    g.eigenleistungCents += z.eigenleistungCents;
    g.deckungsbeitragCents += z.deckungsbeitragCents;
};

const sortiereGruppen = (map: Map<string, NachkalkulationGruppe>): NachkalkulationGruppe[] =>
    Array.from(map.values())
        .map((g) => ({ ...g, margeProzent: prozent(g.deckungsbeitragCents, g.erloeseCents) }))
        // Schlechtester Deckungsbeitrag zuerst: die Ansicht soll mit dem
        // Problem anfangen, nicht mit dem Vorzeigekunden.
        .sort((a, b) => a.deckungsbeitragCents - b.deckungsbeitragCents || a.key.localeCompare(b.key));

/**
 * Nachkalkulation: je Projekt der Deckungsbeitrag, dazu die Verdichtungen
 * nach Kunde, Branche und Region.
 *
 * Branche und Region kommen NICHT aus der Datenbank-RPC, sondern aus dem
 * Kundenstamm — dieselbe Entscheidung wie bei der Karte (crm_map_aggregates:
 * die PLZ→Kreis-Zuordnung steckt in `public/geodata/plz_zuordnung.json`, eine
 * zweite Kopie im Schema waere eine zweite Wahrheit). Ohne uebergebenen
 * Kundenstamm bleiben `jeBranche` und `jeRegion` bei „unbekannt", die
 * Projektzeilen und `jeKunde` sind davon unberuehrt.
 */
export const berechneNachkalkulation = (
    rows: NachkalkulationRow[] | null | undefined,
    kunden?: NachkalkulationKunde[] | null,
    optionen?: NachkalkulationOptionen,
): Nachkalkulation => {
    const level: RegionLevel = optionen?.regionLevel ?? 'bundesland';
    const ohneBranche = optionen?.ohneBrancheLabel ?? 'Branche ungepflegt';
    const ohneRegion = optionen?.ohneRegionLabel ?? 'Region unbekannt';

    const stamm = new Map<string, NachkalkulationKunde>();
    for (const k of kunden || []) if (k?.id) stamm.set(k.id, k);

    const projekte: NachkalkulationZeile[] = [];
    const jeKunde = new Map<string, NachkalkulationGruppe>();
    const jeBranche = new Map<string, NachkalkulationGruppe>();
    const jeRegion = new Map<string, NachkalkulationGruppe>();
    const gesamt = leereGruppe('gesamt', 'Gesamt');
    let begehungenOhneSatz = 0;

    for (const row of rows || []) {
        if (!row?.projectId) continue;
        const kunde = row.kundeId ? stamm.get(row.kundeId) : undefined;
        const treffer = regionKeyForZip(kunde?.zip, optionen?.plzMapping, level);

        const zeile: NachkalkulationZeile = {
            ...row,
            erloeseCents: zahl(row.erloeseCents),
            freelancerKostenCents: zahl(row.freelancerKostenCents),
            eigenleistungCents: zahl(row.eigenleistungCents),
            deckungsbeitragCents: zahl(row.deckungsbeitragCents),
            begehungen: zahl(row.begehungen),
            begehungenOhneSatz: zahl(row.begehungenOhneSatz),
            margeProzent: prozent(zahl(row.deckungsbeitragCents), zahl(row.erloeseCents)),
            branche: kunde?.branche || undefined,
            regionKey: treffer?.key ?? UNBEKANNT_KEY,
        };
        projekte.push(zeile);
        begehungenOhneSatz += zeile.begehungenOhneSatz;
        addiere(gesamt, zeile);

        const kKey = row.kundeId || UNBEKANNT_KEY;
        const kLabel = kunde?.name || row.kundeName || 'Ohne Kunde';
        if (!jeKunde.has(kKey)) jeKunde.set(kKey, leereGruppe(kKey, kLabel));
        addiere(jeKunde.get(kKey)!, zeile);

        const bKey = zeile.branche || UNBEKANNT_KEY;
        if (!jeBranche.has(bKey)) jeBranche.set(bKey, leereGruppe(bKey, zeile.branche || ohneBranche));
        addiere(jeBranche.get(bKey)!, zeile);

        const rKey = zeile.regionKey || UNBEKANNT_KEY;
        const rLabel =
            rKey === UNBEKANNT_KEY ? ohneRegion : treffer?.bundeslandKuerzel || rKey;
        if (!jeRegion.has(rKey)) jeRegion.set(rKey, leereGruppe(rKey, rLabel));
        addiere(jeRegion.get(rKey)!, zeile);
    }

    projekte.sort(
        (a, b) =>
            a.deckungsbeitragCents - b.deckungsbeitragCents ||
            (a.projektNummer || '').localeCompare(b.projektNummer || ''),
    );

    return {
        projekte,
        jeKunde: sortiereGruppen(jeKunde),
        jeBranche: sortiereGruppen(jeBranche),
        jeRegion: sortiereGruppen(jeRegion),
        gesamt: { ...gesamt, margeProzent: prozent(gesamt.deckungsbeitragCents, gesamt.erloeseCents) },
        begehungenOhneSatz,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) Kapazitaetsplanung
// ═══════════════════════════════════════════════════════════════════════════

/**
 * BEGEHUNGS-SOLL — die zentrale Annahme dieses Abschnitts.
 *
 * Ein explizites „Soll je Monat" gibt es im Datenmodell NICHT. Was es gibt:
 *
 *   • `ReportMetadata.intervalConfig` (src/types.ts:561) mit dem Turnus
 *     ('daily' … 'manual') und bei 'custom' der Tagesabstand `customDays`.
 *     Das ist das Feld, aus dem die App auch den naechsten Termin rechnet
 *     (dateService.calculateNextDueDate).
 *   • `ProjectGlobalData.startDate`/`endDate` bzw. die Spalten
 *     `projects.start_date`/`end_date` als Laufzeit.
 *   • `ProjectGlobalData.onHoldUntil` als Ruhezeit.
 *   • `ProjectScope.totalInspections` als vertragliche Gesamtzahl — bewusst
 *     NICHT verwendet: sie ist selten gepflegt und sagt nichts darueber, WANN
 *     die Begehungen anfallen.
 *
 * Das Soll wird deshalb aus dem Turnus abgeleitet: Turnus → Begehungen je
 * Monat, anteilig ueber die Tage, an denen das Projekt im Monat tatsaechlich
 * laeuft. Umrechnungsbasis ist wie in quoteLogic.phaseInspectionCount ein
 * Monat von 30,44 Tagen; 'daily' rechnet mit Arbeitstagen (dort `workingDays`,
 * hier der Mittelwert ARBEITSTAGE_PRO_MONAT).
 *
 * 'manual' liefert 0 — ein manuell terminiertes Projekt hat kein ableitbares
 * Soll. Solche Projekte werden nicht still verschluckt, sondern als
 * `projekteOhneIntervall` gezaehlt.
 */
export const begehungenProMonat = (config?: IntervalConfig | null): number => {
    const type: IntervalType | undefined = config?.type;
    switch (type) {
        case 'daily':
            return ARBEITSTAGE_PRO_MONAT;
        case 'weekly':
            return TAGE_PRO_MONAT / 7;
        case 'biweekly':
            return TAGE_PRO_MONAT / 14;
        case 'twice_monthly':
            return 2;
        case 'monthly':
            return 1;
        case 'custom': {
            const tage = config?.customDays && config.customDays > 0 ? config.customDays : 0;
            return tage > 0 ? TAGE_PRO_MONAT / tage : 0;
        }
        default:
            // 'manual' und „gar kein Intervall".
            return 0;
    }
};

/**
 * Erste alleinstehende 5-stellige Zahl aus einem Freitext = deutsche PLZ.
 *
 * Spiegel der SQL-Funktion `crm_map_zip` (crm_map_aggregates.sql): Projekte
 * haben keine PLZ-Spalte, ihre Adresse steht als Freitext in
 * `data.metadata.projectAddress`. Die Wortgrenzen verhindern, dass eine
 * Hausnummer oder die ersten fuenf Stellen einer Telefonnummer als PLZ
 * durchgehen. Heuristik — sie entscheidet ueber eine Ampelfarbe, nicht ueber
 * einen Beleg.
 */
export const plzAusText = (text?: string | null): string | null => {
    const treffer = String(text ?? '').match(/(?:^|\D)(\d{5})(?:\D|$)/);
    return treffer ? treffer[1] : null;
};

/**
 * KAPAZITAETS-ANNAHME.
 *
 * Wie viele Begehungen ein Koordinator im Monat schafft, steht nirgends im
 * Datenmodell — es gibt weder Stundenzettel noch ein Pensum am Profil. Diese
 * Vorgaben sind der Startwert der Ansicht und dort ueberschreibbar; sie sind
 * bewusst als Konstante benannt und nicht in der Komponente vergraben, damit
 * die Annahme sichtbar bleibt.
 *
 * 20 Begehungen/Monat interner Koordinator = etwa eine je Arbeitstag, mit
 * Puffer fuer Planung, Berichte und Fahrzeiten. 8 je Freelancer, weil ein
 * Freelancer nebenberuflich und auftragsbezogen arbeitet.
 */
export const KAPAZITAET_KOORDINATOR_DEFAULT = 20;
export const KAPAZITAET_FREELANCER_DEFAULT = 8;

export type Ampel = 'gruen' | 'gelb' | 'rot';

export interface KapazitaetProjekt {
    id: string;
    name?: string;
    intervalConfig?: IntervalConfig | null;
    /** Laufzeit. Fehlt der Beginn, zaehlt das Projekt ab Rasteranfang. */
    startDate?: string;
    endDate?: string;
    /** Ruht bis einschliesslich diesem Tag (ProjectGlobalData.onHoldUntil). */
    onHoldUntil?: string;
    /** Freier Regionsschluessel (z. B. Bundeslandkuerzel). */
    region?: string;
}

export interface KapazitaetAssignment {
    projectId: string;
    zeitraumVon: string;
    zeitraumBis: string;
    status: string;
}

export interface KapazitaetKoordinator {
    id: string;
    name?: string;
    region?: string;
    /** Wie viele Begehungen dieser Mensch im Monat schafft. */
    begehungenProMonat: number;
}

export interface KapazitaetFreelancer extends KapazitaetKoordinator {
    /** Nur einsatzbereite Freelancer zaehlen (freelancerLogic.istEinsatzbereit). */
    einsatzbereit?: boolean;
}

export interface KapazitaetOptionen {
    /** Erster Monat des Rasters, 'YYYY-MM'. Default: Monat von `heute`. */
    vonMonat?: string;
    /** Laenge des Rasters in Monaten. Default 12. */
    monate?: number;
    /** Heute, fuer den Default von `vonMonat`. */
    heute?: string;
    freelancer?: KapazitaetFreelancer[] | null;
}

export interface KapazitaetRegion {
    region: string;
    sollBegehungen: number;
    vergebenBegehungen: number;
    internNoetig: number;
    kapazitaetIntern: number;
    kapazitaetFreelancer: number;
    ampel: Ampel;
    /** > 0 = so viele Begehungen finden keinen Kopf. */
    fehlendeBegehungen: number;
}

export interface KapazitaetMonat extends KapazitaetRegion {
    monat: string;
    regionen: KapazitaetRegion[];
}

export interface Kapazitaet {
    monate: KapazitaetMonat[];
    /** Alle roten Monat/Region-Paare — Grundlage fuer „Freelancer akquirieren". */
    engpaesse: { monat: string; region: string; fehlendeBegehungen: number }[];
    /** Projekte mit Laufzeit im Raster, aber ohne ableitbares Intervall. */
    projekteOhneIntervall: number;
    kapazitaetInternGesamt: number;
    kapazitaetFreelancerGesamt: number;
}

/** Status, in denen ein Auftrag die Begehung tatsaechlich bindet. */
const BINDENDE_ASSIGNMENT_STATUS = new Set([
    'angeboten',
    'angenommen',
    'durchgefuehrt',
    'bericht_eingereicht',
    'nachbesserung',
    'geprueft',
    'abgerechnet',
]);

const REGION_UNBEKANNT = 'unbekannt';

const ampelFuer = (
    internNoetig: number,
    kapIntern: number,
    kapFreelancer: number,
): Ampel => {
    if (internNoetig <= kapIntern) return 'gruen';
    if (internNoetig <= kapIntern + kapFreelancer) return 'gelb';
    return 'rot';
};

const leereRegion = (region: string): KapazitaetRegion => ({
    region,
    sollBegehungen: 0,
    vergebenBegehungen: 0,
    internNoetig: 0,
    kapazitaetIntern: 0,
    kapazitaetFreelancer: 0,
    ampel: 'gruen',
    fehlendeBegehungen: 0,
});

/**
 * Kapazitaetsampel je Monat und Region.
 *
 * Rechenweg je Monat:
 *   Soll        = Σ Projekte: Begehungen/Monat × (Tage im Monat, an denen das
 *                 Projekt laeuft) / (Tage im Monat)
 *   Vergeben    = derselbe Anteil, aber nur fuer die Tage, die von einem
 *                 bindenden Begehungsauftrag abgedeckt sind
 *   Intern noetig = Soll − Vergeben
 *   Ampel       = gruen, solange die eigenen Koordinatoren reichen; gelb,
 *                 solange einsatzbereite Freelancer die Luecke schliessen
 *                 koennen; rot, wenn beides zusammen nicht reicht.
 *
 * Warum anteilig und nicht „ganzer Monat oder gar nicht": ein Projekt, das am
 * 25. beginnt, erzeugt im Startmonat nicht denselben Aufwand wie ein
 * durchlaufendes. Die Ungenauigkeit waere genau in den Monaten am groessten,
 * in denen viele Projekte anlaufen — also dort, wo die Ampel gebraucht wird.
 *
 * Die Regionszuordnung kommt vom PROJEKT (`region`), die Kapazitaet von
 * Koordinator und Freelancer. Ein Koordinator ohne Region zaehlt in JEDER
 * Region mit — er ist nicht ortsgebunden, und ihn nur in „unbekannt" zu
 * fuehren, faerbte alle echten Regionen faelschlich rot.
 */
export const berechneKapazitaet = (
    projekte: KapazitaetProjekt[] | null | undefined,
    assignments: KapazitaetAssignment[] | null | undefined,
    koordinatoren: KapazitaetKoordinator[] | null | undefined,
    optionen?: KapazitaetOptionen,
): Kapazitaet => {
    // Rein: kein Date.now(). Ohne `heute` UND ohne `vonMonat` gibt es keinen
    // Bezugspunkt — dann beginnt das Raster bei der Epoche und die Ansicht
    // zeigt sichtbar Unsinn, statt still ein anderes Ergebnis zu liefern.
    const heute = tag(optionen?.heute);
    const startMonat = optionen?.vonMonat || (heute ? monatVonTag(heute) : '1970-01');
    const anzahl = Math.max(1, Math.round(optionen?.monate ?? 12));

    const freelancer = (optionen?.freelancer || []).filter((f) => f?.einsatzbereit !== false);

    const kapInternGesamt = (koordinatoren || []).reduce(
        (s, k) => s + Math.max(0, zahl(k?.begehungenProMonat)),
        0,
    );
    const kapFreelancerGesamt = freelancer.reduce(
        (s, f) => s + Math.max(0, zahl(f?.begehungenProMonat)),
        0,
    );

    // Kapazitaet je Region: ortsgebundene Koepfe auf ihre Region, ortslose auf
    // alle (siehe Kommentar oben).
    const kapInternJeRegion = new Map<string, number>();
    const kapFreelancerJeRegion = new Map<string, number>();
    let kapInternOrtslos = 0;
    let kapFreelancerOrtslos = 0;
    for (const k of koordinatoren || []) {
        const wert = Math.max(0, zahl(k?.begehungenProMonat));
        if (k?.region) kapInternJeRegion.set(k.region, (kapInternJeRegion.get(k.region) || 0) + wert);
        else kapInternOrtslos += wert;
    }
    for (const f of freelancer) {
        const wert = Math.max(0, zahl(f?.begehungenProMonat));
        if (f?.region) kapFreelancerJeRegion.set(f.region, (kapFreelancerJeRegion.get(f.region) || 0) + wert);
        else kapFreelancerOrtslos += wert;
    }

    const assignmentsJeProjekt = new Map<string, KapazitaetAssignment[]>();
    for (const a of assignments || []) {
        if (!a?.projectId || !BINDENDE_ASSIGNMENT_STATUS.has(a.status)) continue;
        const liste = assignmentsJeProjekt.get(a.projectId);
        if (liste) liste.push(a);
        else assignmentsJeProjekt.set(a.projectId, [a]);
    }

    const rasterVon = monatsStart(startMonat);
    const rasterBis = monatsEnde(monatPlus(startMonat, anzahl - 1));

    let projekteOhneIntervall = 0;
    for (const p of projekte || []) {
        if (!p?.id) continue;
        const laeuftImRaster = ueberlappungTage(
            tag(p.startDate),
            tag(p.endDate),
            rasterVon,
            rasterBis,
        ) > 0;
        if (laeuftImRaster && begehungenProMonat(p.intervalConfig) <= 0) projekteOhneIntervall += 1;
    }

    const monate: KapazitaetMonat[] = [];
    const engpaesse: Kapazitaet['engpaesse'] = [];

    for (let i = 0; i < anzahl; i++) {
        const monat = monatPlus(startMonat, i);
        const mVon = monatsStart(monat);
        const mBis = monatsEnde(monat);
        const mTage = tageImMonat(monat);

        const regionen = new Map<string, KapazitaetRegion>();
        let sollGesamt = 0;
        let vergebenGesamt = 0;

        for (const p of projekte || []) {
            if (!p?.id) continue;
            const proMonat = begehungenProMonat(p.intervalConfig);
            if (proMonat <= 0) continue;

            // Ruhezeit schiebt den effektiven Beginn nach hinten: onHoldUntil
            // ist der letzte Ruhetag, gearbeitet wird ab dem Tag danach.
            const hold = tag(p.onHoldUntil);
            const start = hold ? plusTage(hold, 1) : undefined;
            const effektiverStart =
                start && (!tag(p.startDate) || start > tag(p.startDate)!) ? start : tag(p.startDate);

            const laufTage = ueberlappungTage(effektiverStart, tag(p.endDate), mVon, mBis);
            if (laufTage <= 0) continue;

            const soll = (proMonat * laufTage) / mTage;

            // Vergeben: der Anteil der Lauftage, der von bindenden Auftraegen
            // abgedeckt ist. Ueberlappende Auftraege werden NICHT addiert
            // (sonst waere „vergeben" groesser als das Soll) — es zaehlt die
            // groesste Einzelabdeckung.
            let abgedeckt = 0;
            for (const a of assignmentsJeProjekt.get(p.id) || []) {
                const tageA = ueberlappungTage(
                    spaeterer(effektiverStart, tag(a.zeitraumVon)),
                    frueherer(tag(p.endDate), tag(a.zeitraumBis)),
                    mVon,
                    mBis,
                );
                if (tageA > abgedeckt) abgedeckt = tageA;
            }
            const vergeben = (proMonat * Math.min(abgedeckt, laufTage)) / mTage;

            const regionKey = p.region || REGION_UNBEKANNT;
            if (!regionen.has(regionKey)) regionen.set(regionKey, leereRegion(regionKey));
            const r = regionen.get(regionKey)!;
            r.sollBegehungen += soll;
            r.vergebenBegehungen += vergeben;

            sollGesamt += soll;
            vergebenGesamt += vergeben;
        }

        const regionListe = Array.from(regionen.values()).map((r) => {
            const kapIntern = (kapInternJeRegion.get(r.region) || 0) + kapInternOrtslos;
            const kapFree = (kapFreelancerJeRegion.get(r.region) || 0) + kapFreelancerOrtslos;
            const internNoetig = Math.max(0, r.sollBegehungen - r.vergebenBegehungen);
            const ampel = ampelFuer(internNoetig, kapIntern, kapFree);
            const eintrag: KapazitaetRegion = {
                ...r,
                internNoetig,
                kapazitaetIntern: kapIntern,
                kapazitaetFreelancer: kapFree,
                ampel,
                fehlendeBegehungen: Math.max(0, internNoetig - kapIntern - kapFree),
            };
            if (ampel === 'rot') {
                engpaesse.push({
                    monat,
                    region: r.region,
                    fehlendeBegehungen: eintrag.fehlendeBegehungen,
                });
            }
            return eintrag;
        });
        regionListe.sort((a, b) => b.internNoetig - a.internNoetig || a.region.localeCompare(b.region));

        const internNoetig = Math.max(0, sollGesamt - vergebenGesamt);
        monate.push({
            monat,
            region: 'gesamt',
            sollBegehungen: sollGesamt,
            vergebenBegehungen: vergebenGesamt,
            internNoetig,
            kapazitaetIntern: kapInternGesamt,
            kapazitaetFreelancer: kapFreelancerGesamt,
            ampel: ampelFuer(internNoetig, kapInternGesamt, kapFreelancerGesamt),
            fehlendeBegehungen: Math.max(0, internNoetig - kapInternGesamt - kapFreelancerGesamt),
            regionen: regionListe,
        });
    }

    return {
        monate,
        engpaesse,
        projekteOhneIntervall,
        kapazitaetInternGesamt: kapInternGesamt,
        kapazitaetFreelancerGesamt: kapFreelancerGesamt,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) Liquiditaetsvorschau
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zahlungsmoral-Malus in TAGEN.
 *
 * `customers.rating_zahlungsmoral` ist eine Schulnote von 1 bis 5, wobei 5
 * das beste Rating ist (CustomerRatingStars: fuenf Sterne). Der Malus ist eine
 * fachliche Annahme dieses Moduls, keine gemessene Groesse — deshalb steht er
 * hier (getestet, in einer Zeile aenderbar) und nicht im SQL-Skript, das nur
 * ueber den SQL-Editor erreichbar ist.
 *
 * Wer statt der Annahme die MESSUNG will: `customersByPaymentDelay` in
 * invoiceStats.ts liefert den tatsaechlichen Ø-Verzug je Kunde. Bewusst nicht
 * verdrahtet — sie braucht alle Belege und Zahlungen im Browser, und genau das
 * soll die Liquiditaetsansicht nicht laden.
 */
export const ZAHLUNGSMORAL_MALUS_TAGE: Record<number, number> = {
    5: 0,
    4: 3,
    3: 7,
    2: 14,
    1: 21,
};

/** Ohne Rating: der Malus der Mitte. Optimismus waere hier teuer. */
export const ZAHLUNGSMORAL_MALUS_DEFAULT = 7;

export const malusTage = (rating?: number | null): number => {
    const r = Math.round(zahl(rating));
    return ZAHLUNGSMORAL_MALUS_TAGE[r] ?? ZAHLUNGSMORAL_MALUS_DEFAULT;
};

/** Eine Zeile aus der RPC `erp_liquiditaet` (bereits camelCase gemappt). */
export interface LiquiditaetsBeleg {
    id: string;
    art: 'rechnung' | 'gutschrift';
    nummer?: string;
    partner?: string;
    faelligAm?: string;
    /** Positiv = erwarteter Eingang, negativ = Auszahlung. */
    betragCents: number;
    /** Nur bei Rechnungen; 1–5, 5 = beste Zahlungsmoral. */
    zahlungsmoral?: number | null;
}

export interface LiquiditaetsWoche {
    /** 1-basiert, damit die Beschriftung „KW 1 der Vorschau" stimmt. */
    index: number;
    von: string;
    bis: string;
    eingangCents: number;
    auszahlungCents: number;
    nettoCents: number;
    kumuliertCents: number;
}

export interface Liquiditaet {
    wochen: LiquiditaetsWoche[];
    startsaldoCents: number;
    /** Betraege, die vor `heute` faellig waren — in Woche 1 verbucht. */
    ueberfaelligCents: number;
    /** Faellig nach dem Raster — nicht verbucht, aber ausgewiesen. */
    spaeterCents: number;
    /** Ohne Faelligkeitsdatum — nicht verbucht, aber ausgewiesen. */
    ohneFaelligkeitCents: number;
    minKumuliertCents: number;
    /** Index der ersten Woche unter null; null = keine Unterdeckung. */
    ersteUnterdeckung: number | null;
}

export interface LiquiditaetsOptionen {
    /** Default 13 — ein Quartal. */
    wochen?: number;
    /** Bekanntes Bankguthaben zum Start. Default 0 (reine Bewegungsrechnung). */
    startsaldoCents?: number;
}

export const LIQUIDITAET_WOCHEN = 13;

/**
 * Wochenraster ueber 13 Wochen: erwartete Eingaenge minus Auszahlungen,
 * kumuliert.
 *
 * Festlegungen:
 *
 * • EINGAENGE werden um den Zahlungsmoral-Malus nach hinten geschoben
 *   (`malusTage`). Ein Kunde mit Rating 1 zahlt erfahrungsgemaess drei Wochen
 *   nach Faelligkeit — ihn puenktlich einzuplanen erzeugt genau die
 *   Fehlplanung, die diese Ansicht verhindern soll.
 *
 * • AUSZAHLUNGEN bekommen KEINEN Malus. Wir sind der Schuldner; die eigene
 *   Zahlung spaeter anzusetzen, waere eine Beschoenigung.
 *
 * • Was vor `heute` faellig war, faellt nicht heraus, sondern landet in
 *   Woche 1 — das Geld ist ja noch da. Der Betrag steht zusaetzlich in
 *   `ueberfaelligCents`, damit sichtbar bleibt, wie viel davon Altbestand ist.
 *
 * • Was hinter das Raster faellt oder gar kein Faelligkeitsdatum hat, wird
 *   NICHT verbucht, aber als eigene Zahl zurueckgegeben. Stilles Weglassen
 *   waere ein unsichtbarer Datenverlust (gleiche Regel wie „unbekannt" auf der
 *   Karte).
 */
export const berechneLiquiditaet = (
    rechnungen: LiquiditaetsBeleg[] | null | undefined,
    gutschriften: LiquiditaetsBeleg[] | null | undefined,
    heute: string,
    optionen?: LiquiditaetsOptionen,
): Liquiditaet => {
    const anzahl = Math.max(1, Math.round(optionen?.wochen ?? LIQUIDITAET_WOCHEN));
    const startsaldoCents = Math.round(zahl(optionen?.startsaldoCents));
    const heuteTag = tag(heute) || '1970-01-01';
    const rasterStart = montagDerWoche(heuteTag);

    const wochen: LiquiditaetsWoche[] = [];
    for (let i = 0; i < anzahl; i++) {
        const von = plusTage(rasterStart, i * 7);
        wochen.push({
            index: i + 1,
            von,
            bis: plusTage(von, 6),
            eingangCents: 0,
            auszahlungCents: 0,
            nettoCents: 0,
            kumuliertCents: 0,
        });
    }
    const rasterEnde = wochen[wochen.length - 1].bis;

    let ueberfaelligCents = 0;
    let spaeterCents = 0;
    let ohneFaelligkeitCents = 0;

    const verbuche = (beleg: LiquiditaetsBeleg, mitMalus: boolean): void => {
        const betrag = Math.round(zahl(beleg?.betragCents));
        if (!betrag) return;

        const faellig = tag(beleg?.faelligAm);
        if (!faellig || msVonTag(faellig) === null) {
            ohneFaelligkeitCents += betrag;
            return;
        }

        const erwartet = mitMalus ? plusTage(faellig, malusTage(beleg.zahlungsmoral)) : faellig;
        if (erwartet > rasterEnde) {
            spaeterCents += betrag;
            return;
        }
        if (erwartet < heuteTag) ueberfaelligCents += betrag;

        // Vor dem Raster (ueberfaellig) => Woche 1.
        const versatz = Math.floor(
            ((msVonTag(erwartet) ?? 0) - (msVonTag(rasterStart) ?? 0)) / (7 * TAG_MS),
        );
        const woche = wochen[Math.min(anzahl - 1, Math.max(0, versatz))];
        if (betrag >= 0) woche.eingangCents += betrag;
        else woche.auszahlungCents += betrag;
    };

    for (const r of rechnungen || []) verbuche(r, true);
    for (const g of gutschriften || []) verbuche(g, false);

    let kumuliert = startsaldoCents;
    let minKumuliertCents = startsaldoCents;
    let ersteUnterdeckung: number | null = null;
    for (const w of wochen) {
        w.nettoCents = w.eingangCents + w.auszahlungCents;
        kumuliert += w.nettoCents;
        w.kumuliertCents = kumuliert;
        if (kumuliert < minKumuliertCents) minKumuliertCents = kumuliert;
        if (kumuliert < 0 && ersteUnterdeckung === null) ersteUnterdeckung = w.index;
    }

    return {
        wochen,
        startsaldoCents,
        ueberfaelligCents,
        spaeterCents,
        ohneFaelligkeitCents,
        minKumuliertCents,
        ersteUnterdeckung,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5) Auftragswert-Vorschlag (Angebotsannahme)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Angebots-Nettosumme (Euro, `quotes.net_total` bzw.
 * `calculateQuoteTotals().netTotal`) als Auftragswert in CENT.
 *
 * Die eine Stelle im Haus, an der Euro zu Cent wird: `quotes.net_total` ist
 * `numeric(12,2)` in Euro, `project_order_values.order_value_cents` ist
 * bigint in Cent. `Math.round` und nicht `Math.trunc`, damit 1234.565 nicht
 * auf 123456 abgeschnitten wird.
 *
 * NETTO und nicht brutto: der Auftragsbestand wird gegen fakturiertes NETTO
 * gerechnet (erp_auftragsbestand), Umsatzsteuer ist kein Auftragswert.
 */
export const auftragswertCentsAusAngebot = (netTotalEuro?: number | null): number => {
    const n = zahl(netTotalEuro);
    return n > 0 ? Math.round(n * 100) : 0;
};

/**
 * Eingabe des Auftragswert-Feldes (Euro als Text, deutsche Schreibweise) in
 * Cent. Gibt null zurueck, wenn die Eingabe keine Zahl ist — der Aufrufer
 * unterscheidet damit „leer gelassen" von „0 EUR bewusst gesetzt".
 */
export const auftragswertCentsAusEingabe = (eingabe: string): number | null => {
    const s = String(eingabe ?? '').trim();
    if (!s) return null;
    // Deutsche Eingabe: Punkt ist Tausender, Komma ist Dezimaltrenner.
    const norm = s.replace(/\./g, '').replace(',', '.').replace(/\s|€/g, '');
    const n = Number(norm);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
};

/** Cent als deutscher Euro-Betrag ohne Waehrungszeichen (Eingabefeld-Vorbelegung). */
export const centsAlsEingabe = (cents?: number | null): string => {
    const c = Math.round(zahl(cents));
    return (c / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
